import Swiper from 'swiper';
import { EffectCoverflow, Navigation, Pagination, Autoplay } from 'swiper/modules';
import { SWIPER_CONFIG } from 'js/config/swiper';

/**
 * Swiper factory
 *
 * @param {mixed} container Selector or HTMLElement
 * @param {object} config Additional or override config
 * @returns {Swiper} Swiper instance
 */
function createSwiper(container, config = {}) {
    if (!container) {
        return;
    }

    const wrapper = container.parentNode;
    const prevButton = wrapper.querySelector('.swiper-button-prev');
    const nextButton = wrapper.querySelector('.swiper-button-next');

    if (prevButton && prevButton.tagName.toLowerCase() !== 'button') {
        const newPrevButton = document.createElement('button');
        newPrevButton.className = prevButton.className;
        newPrevButton.innerHTML = prevButton.innerHTML;
        newPrevButton.setAttribute('type', 'button');
        newPrevButton.setAttribute('aria-label', 'Previous slide');
        prevButton.parentNode.replaceChild(newPrevButton, prevButton);
    }

    if (nextButton && nextButton.tagName.toLowerCase() !== 'button') {
        const newNextButton = document.createElement('button');
        newNextButton.className = nextButton.className;
        newNextButton.innerHTML = nextButton.innerHTML;
        newNextButton.setAttribute('type', 'button');
        newNextButton.setAttribute('aria-label', 'Next slide');
        nextButton.parentNode.replaceChild(newNextButton, nextButton);
    }

    const autoPlayEnabled = container.classList.contains('carousel--autoplay');

    return new Swiper(container, {
        ...SWIPER_CONFIG,

        // Container defaults
        ...{
            modules: [
                EffectCoverflow,
                Navigation,
                Pagination,
                ...(autoPlayEnabled ? [Autoplay] : []),
            ],
            navigation: {
                nextEl: wrapper.querySelector('.swiper-button-next'),
                prevEl: wrapper.querySelector('.swiper-button-prev'),
            },
            pagination: {
                el: wrapper.querySelector('.swiper-pagination'),
                clickable: true,
            },
        },

        ...(autoPlayEnabled ? {
            autoplay: {
                delay: 4000,
            },
        } : {}),

        // Coverflow carousel
        ...(
            container.classList.contains('carousel--coverflow') ? {
                centeredSlides: true,
                coverflowEffect: {
                    rotate: 0,
                    scale: 0.8,
                    slideShadows: false,
                    stretch: 50,
                },
                effect: 'coverflow',
                loop: true,
                slidesPerView: 'auto',
                breakpoints: {
                    768: {
                        coverflowEffect: {
                            stretch: 75,
                        },
                    },
                    1280: {
                        coverflowEffect: {
                            stretch: 125,
                        },
                    },
                },
            } : {}
        ),

        // Portrait shorts carousel: narrow 9:16 slides show several at once,
        // which the coverflow scale (uneven gaps) and loop (asymmetric) don't
        // suit. So it runs as a plain, left-aligned slide carousel with a fixed
        // gap between equal slides. Overrides the coverflow config above.
        ...(
            container.classList.contains('carousel--portrait') ? {
                effect: 'slide',
                loop: false,
                centeredSlides: false,
                spaceBetween: 16,
                breakpoints: {},
            } : {}
        ),

        // Cards carousel
        ...(
            container.classList.contains('carousel--cards') ? {
                slidesPerView: 1,
                centeredSlides: true,
                spaceBetween: 12,
                navigation: {
                    nextEl: wrapper.querySelector('.swiper-button-next'),
                    prevEl: wrapper.querySelector('.swiper-button-prev'),
                },
                breakpoints: {
                    1024: {
                        centeredSlides: false,
                        spaceBetween: 20,
                        slidesPerView: container.dataset.maxSlidesPerView ?? 3,
                    },
                },
            } : {}
        ),

        // Factory config
        ...config,
    });
}

// Instantiate
document.querySelectorAll('.swiper').forEach((container) => {
    createSwiper(container);
});
